if (!customElements.get('yozakura-product-description')) {
  customElements.define('yozakura-product-description', class extends HTMLElement {
    connectedCallback() {
      if (this.built) {
        this.resizeObserver.observe(this.track);
        return;
      }
      this.watch = new MutationObserver(() => this.build());
      this.watch.observe(this, { childList: true, subtree: true });
      queueMicrotask(() => this.build());
    }

    disconnectedCallback() {
      this.watch?.disconnect();
      this.resizeObserver?.disconnect();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
    }

    build() {
      if (this.built || !this.isConnected) return;
      const images = Array.from(this.querySelectorAll('img'));
      if (images.length < 2) return;
      this.built = true;
      this.watch.disconnect();

      const commonDetails = images[0].closest('details');
      const scope = commonDetails && images.every(image => image.closest('details') === commonDetails)
        ? commonDetails : this;
      let insertion = images[0];
      while (insertion.parentElement && insertion.parentElement !== scope) {
        insertion = insertion.parentElement;
      }

      const gallery = document.createElement('section');
      gallery.className = 'yz-detail-gallery';
      gallery.setAttribute('role', 'region');
      gallery.setAttribute('aria-label', this.dataset.galleryLabel);
      const track = document.createElement('div');
      track.className = 'yz-detail-gallery__track';
      track.dir = 'ltr';
      track.tabIndex = 0;
      track.setAttribute('aria-label', this.dataset.galleryLabel);
      gallery.append(track);
      if (insertion.textContent.trim()) insertion.after(gallery);
      else insertion.before(gallery);

      const slides = images.map((image, index) => {
        const slide = document.createElement('div');
        slide.className = 'yz-detail-gallery__slide';
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-label', this.dataset.imageLabel + ' ' + (index + 1) + ' ' + this.dataset.ofLabel + ' ' + images.length);
        let item = image;
        if (image.parentElement?.tagName === 'PICTURE') item = image.parentElement;
        if (item.parentElement?.tagName === 'A' && item.parentElement.querySelectorAll('img').length === 1) {
          item = item.parentElement;
        }
        const figure = item.closest('figure');
        if (figure && figure !== scope && figure.querySelectorAll('img').length === 1) item = figure;
        let empty = item.parentElement;
        slide.append(item);
        track.append(slide);
        image.loading = 'lazy';
        image.decoding = 'async';
        while (empty && empty !== scope && this.contains(empty)
          && /^(P|DIV|SPAN|A|FIGURE|PICTURE)$/.test(empty.tagName)
          && !empty.textContent.trim()
          && !empty.querySelector('img,video,iframe,table,svg,input,button,summary')
          && !empty.contains(gallery)) {
          const parent = empty.parentElement;
          empty.remove();
          empty = parent;
        }
        return slide;
      });

      const controls = document.createElement('div');
      controls.className = 'yz-detail-gallery__controls';
      controls.dir = 'ltr';
      const button = (label, arrow) => {
        const node = document.createElement('button');
        node.type = 'button';
        node.className = 'yz-detail-gallery__arrow';
        node.setAttribute('aria-label', label);
        const icon = document.createElement('span');
        icon.setAttribute('aria-hidden', 'true');
        icon.textContent = arrow;
        node.append(icon);
        return node;
      };
      const previous = button(this.dataset.previousLabel, '←');
      const next = button(this.dataset.nextLabel, '→');
      const counter = document.createElement('span');
      counter.className = 'yz-detail-gallery__counter';
      counter.setAttribute('aria-live', 'polite');
      counter.setAttribute('aria-atomic', 'true');
      controls.append(previous, counter, next);
      gallery.append(controls);
      this.track = track;
      let active = 0;

      const update = () => {
        if (track.clientWidth > 0) {
          active = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
        }
        counter.textContent = (active + 1) + ' / ' + slides.length;
        previous.disabled = active === 0;
        next.disabled = active === slides.length - 1;
        slides.forEach((slide, index) => {
          slide.inert = index !== active;
          slide.setAttribute('aria-hidden', String(index !== active));
        });
      };
      const go = index => {
        const target = Math.max(0, Math.min(slides.length - 1, index));
        const behavior = matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
        track.scrollTo({ left: target * track.clientWidth, behavior });
      };
      previous.addEventListener('click', () => go(active - 1));
      next.addEventListener('click', () => go(active + 1));
      track.addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        event.preventDefault();
        go(event.key === 'Home' ? 0 : event.key === 'End' ? slides.length - 1 : active + (event.key === 'ArrowRight' ? 1 : -1));
      });
      track.addEventListener('scroll', () => {
        if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
        this.scrollFrame = requestAnimationFrame(update);
      }, { passive: true });
      this.resizeObserver = new ResizeObserver(() => {
        if (track.clientWidth > 0) {
          track.scrollTo({ left: active * track.clientWidth, behavior: 'auto' });
          update();
        }
      });
      this.resizeObserver.observe(track);
      update();
    }
  });
}
